// The morning brief and the weekly story. These template versions work
// offline and without AI; the server can ask Claude to write warmer ones
// from the same numbers.

import { SOCIAL_CATEGORIES } from '../content/socialisation.ts';
import { ageNow } from './age.ts';
import { careState, openCare } from './care.ts';
import { dayStats, durationWithin, sleepWithin } from './status.ts';
import { DAY, addDays, atClock, dayStart, daysBetween, formatDate, toISODate, today } from './time.ts';
import { upcomingAppointments, whenLabel } from './appointments.ts';
import { skillName, weekStart } from './trainingplan.ts';
import { proactiveTips } from './tips.ts';
import { LEVEL_LABEL, healthWatch } from './healthwatch.ts';
import { nightTripPlan, onDuty } from './nightplan.ts';
import type { Appointment, CareItem, Household, LogEvent, Millis, Profile, TrainingWeek } from './types.ts';
import type { Forecast } from './weather.ts';

export interface Brief {
  title: string;
  body: string;
  /** 'ai' when Claude wrote it. */
  via?: 'ai' | 'template';
  date?: string;
}

export interface BriefInput {
  profile?: Profile;
  household?: Household;
  events: LogEvent[];
  care: CareItem[];
  forecast?: Forecast;
  now: Millis;
  appointments?: Appointment[];
  trainingWeeks?: TrainingWeek[];
}

export function templateDailyBrief({ profile, household, events, care, forecast, now, appointments = [], trainingWeeks = [] }: BriefInput): Brief {
  const name = profile?.name || 'Arlo';
  const day = today(now);
  const weekday = new Date(now).toLocaleDateString('en-GB', { weekday: 'long' });
  if (profile?.homeDate && profile.homeDate > day) {
    const n = daysBetween(day, profile.homeDate);
    return { title: n === 1 ? `${name} comes home tomorrow!` : `${n} days until ${name} comes home`, body: 'Check the shopping list and the house rules — and get some sleep while you can.', via: 'template', date: day };
  }
  const age = profile ? ageNow(profile, now) : undefined;
  const parts: string[] = [];
  const y = dayStats(events, dayStart(now, -1));
  const wees = y.weeOutside + y.weeInside;
  const accidents = y.weeInside + y.pooInside;
  if (wees + y.pooOutside + y.pooInside + y.meals > 0) {
    const bits = [
      wees ? `${y.weeOutside}/${wees} wees outside` : '',
      accidents ? `${accidents} accident${accidents > 1 ? 's' : ''}` : wees ? 'no accidents' : '',
      y.meals ? `${y.meals} meal${y.meals > 1 ? 's' : ''}` : '',
      y.sleepMs > 2 * 3_600_000 ? `${Math.round(y.sleepMs / 3_600_000)}h sleep logged` : '',
    ].filter(Boolean);
    parts.push(`Yesterday: ${bits.join(', ')}.`);
  }
  const due = openCare(care).filter((c) => ['due', 'overdue'].includes(careState(c, now)));
  if (due.length) parts.push(due.length <= 2 ? `Today: ${due.map((c) => c.title).join(' and ')}.` : `Today: ${due[0].title}, ${due[1].title} and ${due.length - 2} more care job${due.length > 3 ? 's' : ''}.`);
  // Today's appointments (and tomorrow's early ones) come right after any health flag.
  const booked = upcomingAppointments(appointments, now).filter((a) => toISODate(a.at) === day || (toISODate(a.at) === addDays(day, 1) && new Date(a.at).getHours() < 11));
  if (booked.length) parts.unshift(`${booked.map((a) => `${a.title} ${whenLabel(a.at, now)}`).join(', ').replace(/^./, (c) => c.toUpperCase())}.`);
  const plan = trainingWeeks.find((w) => w.id === weekStart(now));
  if (plan?.skills.length) parts.push(`Training this week: ${plan.skills.map(skillName).join(' and ')}.`);
  // Anything the health watch is flagging leads the brief.
  const flags = healthWatch({ events, care, profile, now }).filter((f) => f.level !== 'watch');
  if (flags.length) parts.unshift(`${LEVEL_LABEL[flags[0].level]}: ${flags[0].title.toLowerCase()}.`);
  if (household) {
    const onToday = onDuty(household, 'day', now);
    const plan = nightTripPlan({ household, events, weeks: age?.weeks ?? 12, now });
    // Tonight's person, even if the brief is read before the night ends (nightEnd after 07:30).
    const tonight = onDuty(household, 'night', atClock(now, '18:00'));
    const bits = [onToday ? `${onToday.name}’s with him today` : '', plan.time ? (tonight ? `${tonight.name}’s on the night trip (${plan.time})` : `night trip at ${plan.time}`) : ''].filter(Boolean);
    if (bits.length) parts.push(`${bits.join('; ').replace(/^./, (c) => c.toUpperCase())}.`);
  }
  const tip = proactiveTips({ profile, household, events, care, forecast, now }).find((t) => t.kind === 'weather') ?? proactiveTips({ profile, household, events, care, forecast, now })[0];
  if (tip) parts.push(`${tip.title}${tip.title.endsWith('.') ? '' : '.'}`);
  if (!parts.length) parts.push('A fresh day. Out first thing, breakfast, then a nap — keep it simple.');
  return { title: `${weekday} with ${name}${age ? ` · ${age.label}` : ''}`, body: parts.join(' '), via: 'template', date: day };
}

export interface WeekSummary {
  /** Week number since he came home (1 = first week). */
  weekHome?: number;
  from: Millis;
  to: Millis;
  toiletPct?: number;
  prevToiletPct?: number;
  accidents: number;
  meals: number;
  sleepHoursPerDay?: number;
  walks: number;
  walkMiles: number;
  walkMinutes: number;
  trainingSessions: number;
  newExperiences: string[];
  weightStart?: number;
  weightEnd?: number;
  photos: number;
  logged: number;
}

/** Numbers for the 7 days ending at local midnight after `now`'s day (i.e. including today). */
export function weekSummary(events: LogEvent[], profile: Profile | undefined, now: Millis): WeekSummary {
  const to = dayStart(now, 1);
  const from = dayStart(now, -6);
  const inWeek = events.filter((e) => e.at >= from && e.at < to);
  const prev = events.filter((e) => e.at >= dayStart(now, -13) && e.at < from);
  const pct = (list: LogEvent[]) => {
    const t = list.filter((e) => e.type === 'wee' || e.type === 'poo') as LogEvent<'wee' | 'poo'>[];
    return t.length ? t.filter((e) => e.data.where === 'outside').length / t.length : undefined;
  };
  const walks = inWeek.filter((e): e is LogEvent<'walk'> => e.type === 'walk' && e.data.auto !== 'pending');
  const weights = inWeek.filter((e): e is LogEvent<'weight'> => e.type === 'weight').sort((a, b) => a.at - b.at);
  const prevWeight = events.filter((e): e is LogEvent<'weight'> => e.type === 'weight' && e.at < from).sort((a, b) => b.at - a.at)[0];
  const seenBefore = new Set(events.filter((e): e is LogEvent<'social'> => e.type === 'social' && e.at < from).map((e) => e.data.itemId));
  const newItems = [...new Set(inWeek.filter((e): e is LogEvent<'social'> => e.type === 'social' && !seenBefore.has(e.data.itemId)).map((e) => e.data.itemId))];
  const label = (id: string) => SOCIAL_CATEGORIES.flatMap((c) => c.items).find((i) => i.id === id)?.label ?? id;
  const days = Math.max(1, Math.min(7, Math.round((Math.min(to, Date.now() > to ? to : Math.max(Date.now(), from + DAY)) - from) / DAY)));
  const homeFrom = profile?.homeDate ? new Date(profile.homeDate + 'T00:00').getTime() : undefined;
  return {
    weekHome: homeFrom !== undefined && now >= homeFrom ? Math.floor((now - homeFrom) / (7 * DAY)) + 1 : undefined,
    from,
    to,
    toiletPct: pct(inWeek),
    prevToiletPct: pct(prev),
    accidents: inWeek.filter((e) => (e.type === 'wee' || e.type === 'poo') && e.data.where === 'inside').length,
    meals: inWeek.filter((e) => e.type === 'meal').length,
    sleepHoursPerDay: inWeek.some((e) => e.type === 'sleep') ? sleepWithin(events, from, Math.min(to, now)) / days / 3_600_000 : undefined,
    walks: walks.length,
    walkMiles: walks.reduce((s, w) => s + (w.data.distanceKm ?? 0), 0) / 1.609344,
    walkMinutes: Math.round(durationWithin(events, 'walk', from, to) / 60_000),
    trainingSessions: inWeek.filter((e) => e.type === 'training').length,
    newExperiences: newItems.map(label),
    weightStart: prevWeight?.data.kg ?? weights[0]?.data.kg,
    weightEnd: weights[weights.length - 1]?.data.kg,
    photos: inWeek.filter((e) => e.type === 'photo').length,
    logged: inWeek.length,
  };
}

export interface WeekStory {
  title: string;
  story: string;
  highlights: string[];
  via?: 'ai' | 'template';
  week?: string;
}

export function templateWeekStory(s: WeekSummary, name = 'Arlo'): WeekStory {
  const highlights: string[] = [];
  if (s.toiletPct !== undefined) {
    const p = Math.round(s.toiletPct * 100);
    const trend = s.prevToiletPct !== undefined ? (s.toiletPct > s.prevToiletPct + 0.03 ? ` (up from ${Math.round(s.prevToiletPct * 100)}%)` : s.toiletPct < s.prevToiletPct - 0.03 ? ` (down from ${Math.round(s.prevToiletPct * 100)}%)` : '') : '';
    highlights.push(`${p}% of toilet trips outside${trend}`);
  }
  if (s.newExperiences.length) highlights.push(`${s.newExperiences.length} new experience${s.newExperiences.length > 1 ? 's' : ''}: ${s.newExperiences.slice(0, 4).join(', ')}${s.newExperiences.length > 4 ? '…' : ''}`);
  if (s.trainingSessions) highlights.push(`${s.trainingSessions} training session${s.trainingSessions > 1 ? 's' : ''}`);
  if (s.walks) highlights.push(`${s.walks} outing${s.walks > 1 ? 's' : ''}${s.walkMiles >= 0.1 ? `, ${s.walkMiles.toFixed(1)} miles` : ''}`);
  if (s.weightEnd !== undefined) highlights.push(s.weightStart !== undefined && s.weightStart !== s.weightEnd ? `${s.weightStart} → ${s.weightEnd} kg` : `${s.weightEnd} kg`);
  if (s.photos) highlights.push(`${s.photos} photo${s.photos > 1 ? 's' : ''}`);
  const title = s.weekHome ? `${name}’s week ${s.weekHome} at home` : `${name}’s week`;
  const range = `${formatDate(s.from)} – ${formatDate(s.to - 1)}`;
  const story = !s.logged
    ? `A quiet week in the log (${range}). That’s fine — logging is optional. A few taps a day makes next week’s story richer.`
    : `${range}. ${highlights.length ? highlights[0][0].toUpperCase() + highlights[0].slice(1) : 'Steady progress'}${s.accidents === 0 && s.toiletPct !== undefined ? ', and not a single accident logged' : ''}. ${s.newExperiences.length ? `He met ${s.newExperiences.slice(0, 2).join(' and ').toLowerCase()} for the first time.` : 'Keep adding new experiences while the socialisation window is open.'}`;
  return { title, story, highlights, via: 'template' };
}
